import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth } from "../../firebase";
import "../../css/AudioTranscription.css";

const MAX_RECORDING_SECONDS = 3600; // 1 hour max
const API_BASE_URL = "https://bluebirddocumentationadmin.pythonanywhere.com";

function AudioTranscription() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadingFileName, setUploadingFileName] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    const [jobDetails, setJobDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState(null);
    const [uid, setUid] = useState(null);
    const [pollingJobId, setPollingJobId] = useState(null);
    
    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    
    // Summarization instructions
    const [summarizationInstructions, setSummarizationInstructions] = useState("");
    
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const navigate = useNavigate();

    // Auth listener
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
            } else {
                navigate("/login");
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // Fetch jobs list
    useEffect(() => {
        if (uid) {
            fetchJobs();
        }
    }, [uid]);

    // Poll for processing jobs
    useEffect(() => {
        if (pollingJobId) {
            const interval = setInterval(() => {
                fetchJobDetails(pollingJobId);
            }, 5000); // Poll every 5 seconds (audio processing takes longer)

            return () => clearInterval(interval);
        }
    }, [pollingJobId]);

    // Cleanup audio URL on unmount
    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioUrl]);

    // Auto-stop recording at max duration
    useEffect(() => {
        if (recordingTime >= MAX_RECORDING_SECONDS && isRecording) {
            stopRecording();
        }
    }, [recordingTime, isRecording]);

    const fetchJobs = async (clearErrorOnProcessing = false) => {
        try {
            setLoading(true);
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                `${API_BASE_URL}/audio/transcribe/jobs/`,
                {
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setJobs(data);
                
                // Check if there's a processing job to poll
                const processingJob = data.find(
                    (job) => job.status === "pending" || job.status === "processing"
                );
                if (processingJob) {
                    setPollingJobId(processingJob.id);
                    // If the job was created despite a server error being shown, clear the
                    // error and auto-open the job so the user can see it's actually processing.
                    if (clearErrorOnProcessing) {
                        setError(null);
                        setSelectedJob((currentSelected) => currentSelected ?? processingJob.id);
                    }
                }
            } else {
                setError("Failed to fetch transcription jobs");
            }
        } catch (err) {
            console.error("Error fetching jobs:", err);
            setError("Error loading jobs");
        } finally {
            setLoading(false);
        }
    };

    const fetchJobDetails = async (jobId) => {
        try {
            setLoadingDetails(true);
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                `${API_BASE_URL}/audio/transcribe/jobs/${jobId}/`,
                {
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setJobDetails(data);

                // Stop polling if job is no longer processing
                if (data.status !== "pending" && data.status !== "processing") {
                    setPollingJobId(null);
                    // Refresh jobs list to update status
                    fetchJobs();
                }
            } else {
                setError("Failed to fetch job details");
            }
        } catch (err) {
            console.error("Error fetching job details:", err);
            setError("Error loading job details");
        } finally {
            setLoadingDetails(false);
        }
    };

    const startRecording = async () => {
        try {
            setError(null);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
            });
            
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { 
                    type: mediaRecorder.mimeType 
                });
                setAudioBlob(audioBlob);
                
                // Create URL for playback
                const url = URL.createObjectURL(audioBlob);
                setAudioUrl(url);
                
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start(1000); // Collect data every second
            setIsRecording(true);
            setRecordingTime(0);
            setAudioBlob(null);
            setAudioUrl(null);

            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (err) {
            console.error("Error starting recording:", err);
            setError("Could not access microphone. Please check permissions.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
    };

    const discardRecording = () => {
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
        }
        setAudioBlob(null);
        setAudioUrl(null);
        setRecordingTime(0);
    };

    const uploadRecording = async () => {
        if (!audioBlob) return;

        try {
            setUploading(true);
            setError(null);
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const formData = new FormData();
            const extension = audioBlob.type.includes('webm') ? 'webm' : 'm4a';
            formData.append('audio_file', audioBlob, `recording_${Date.now()}.${extension}`);
            if (summarizationInstructions.trim()) {
                formData.append('summarization_instructions', summarizationInstructions.trim());
            }

            const response = await fetch(
                `${API_BASE_URL}/audio/transcribe/`,
                {
                    method: "POST",
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                    body: formData,
                }
            );

            if (response.ok) {
                const data = await response.json();
                setPollingJobId(data.id);
                discardRecording();
                fetchJobs();
            } else {
                // The job may have been created on the server before the error occurred.
                // Refresh the jobs list so the user can see it if it was queued.
                let errorMsg = "Failed to upload audio";
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.error || errorMsg;
                } catch (_) {}
                setError(`Your recording was submitted, but the server returned an error: "${errorMsg}". If a job appears in the list below, it is processing normally.`);
                fetchJobs(true);
            }
        } catch (err) {
            console.error("Error uploading audio:", err);
            setError("Error uploading audio file");
        } finally {
            setUploading(false);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            setUploadingFileName(file.name);
            setError(null);
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const formData = new FormData();
            formData.append('audio_file', file);
            if (summarizationInstructions.trim()) {
                formData.append('summarization_instructions', summarizationInstructions.trim());
            }

            const response = await fetch(
                `${API_BASE_URL}/audio/transcribe/`,
                {
                    method: "POST",
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                    body: formData,
                }
            );

            if (response.ok) {
                const data = await response.json();
                setPollingJobId(data.id);
                fetchJobs();
            } else {
                // The job may have been created on the server before the error occurred.
                // Refresh the jobs list so the user can see it if it was queued.
                let errorMsg = "Failed to upload audio";
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.error || errorMsg;
                } catch (_) {}
                setError(`Your file was submitted, but the server returned an error: "${errorMsg}". If a job appears in the list below, it is processing normally.`);
                fetchJobs(true);
            }
        } catch (err) {
            console.error("Error uploading file:", err);
            setError("Error uploading audio file");
        } finally {
            setUploading(false);
            setUploadingFileName(null);
            event.target.value = ''; // Reset file input
        }
    };

    const deleteJob = async (jobId) => {
        if (!window.confirm("Are you sure you want to delete this transcription job?")) {
            return;
        }

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                `${API_BASE_URL}/audio/transcribe/jobs/${jobId}/delete/`,
                {
                    method: "DELETE",
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                }
            );

            if (response.ok) {
                fetchJobs();
                if (selectedJob === jobId) {
                    setSelectedJob(null);
                    setJobDetails(null);
                }
            } else {
                const data = await response.json();
                setError(data.error || "Failed to delete job");
            }
        } catch (err) {
            console.error("Error deleting job:", err);
            setError("Error deleting job");
        }
    };

    const deleteAudioOnly = async (jobId) => {
        if (!window.confirm("Delete the audio file? The transcription will be preserved.")) {
            return;
        }

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                `${API_BASE_URL}/audio/transcribe/jobs/${jobId}/delete-audio/`,
                {
                    method: "POST",
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                }
            );

            if (response.ok) {
                fetchJobDetails(jobId);
                fetchJobs();
            } else {
                const data = await response.json();
                setError(data.error || "Failed to delete audio");
            }
        } catch (err) {
            console.error("Error deleting audio:", err);
            setError("Error deleting audio file");
        }
    };

    const downloadAudio = async (jobId, filename) => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                `${API_BASE_URL}/audio/transcribe/jobs/${jobId}/download-audio/`,
                {
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                }
            );

            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename || `audio_${jobId}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } else {
                setError("Failed to download audio file");
            }
        } catch (err) {
            console.error("Error downloading audio:", err);
            setError("Error downloading audio file");
        }
    };

    const handleJobClick = (jobId) => {
        setSelectedJob(jobId);
        fetchJobDetails(jobId);
    };

    const handleCreateDocument = (jobId) => {
        navigate(`/audio-transcription/create-document/${jobId}`);
    };

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        
        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const formatLanguage = (language) => {
        if (!language) return null;
        const trimmed = String(language).trim();
        return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : null;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return "N/A";
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(2)} MB`;
    };

    const formatDuration = (seconds) => {
        if (!seconds) return "N/A";
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            pending: "AudioTranscription__status-pending",
            processing: "AudioTranscription__status-processing",
            completed: "AudioTranscription__status-completed",
            failed: "AudioTranscription__status-failed",
        };

        return (
            <span className={`AudioTranscription__status-badge ${statusClasses[status] || ""}`}>
                {status.toUpperCase()}
            </span>
        );
    };

    const renderResults = () => {
        if (!jobDetails) return null;

        return (
            <div className="AudioTranscription__results-container">
                {jobDetails.transcript && (
                    <div className="AudioTranscription__result-section">
                        <h3>
                            <i className="fas fa-file-alt"></i> Transcript
                        </h3>
                        <div className="AudioTranscription__transcript-content">
                            {jobDetails.transcript}
                        </div>
                        <button
                            className="AudioTranscription__copy-button"
                            onClick={() => {
                                navigator.clipboard.writeText(jobDetails.transcript);
                            }}
                        >
                            <i className="fas fa-copy"></i> Copy Transcript
                        </button>
                    </div>
                )}

                {jobDetails.summary && (
                    <div className="AudioTranscription__result-section">
                        <h3>
                            <i className="fas fa-brain"></i> AI Summary
                        </h3>
                        <div className="AudioTranscription__summary-content">
                            {jobDetails.summary}
                        </div>
                        <button
                            className="AudioTranscription__copy-button"
                            onClick={() => {
                                navigator.clipboard.writeText(jobDetails.summary);
                            }}
                        >
                            <i className="fas fa-copy"></i> Copy Summary
                        </button>
                    </div>
                )}

                {/* Create Document Action */}
                {jobDetails.status === "completed" && (jobDetails.transcript || jobDetails.summary) && (
                    <div className="AudioTranscription__create-document-section">
                        <h3>
                            <i className="fas fa-file-plus"></i> Create Document
                        </h3>
                        <p>Turn this transcription into a document that you can save and share.</p>
                        <button
                            className="AudioTranscription__create-document-button"
                            onClick={() => handleCreateDocument(jobDetails.id)}
                        >
                            <i className="fas fa-plus-circle"></i> Create Document from Transcription
                        </button>
                    </div>
                )}

                {/* Audio file actions */}
                {jobDetails.status === "completed" && (
                    <div className="AudioTranscription__audio-actions">
                        {!jobDetails.audio_deleted_at ? (
                            <>
                                <button
                                    className="AudioTranscription__action-button AudioTranscription__download-button"
                                    onClick={() => downloadAudio(jobDetails.id, jobDetails.original_filename)}
                                >
                                    <i className="fas fa-download"></i> Download Audio
                                </button>
                                <button
                                    className="AudioTranscription__action-button AudioTranscription__delete-audio-button"
                                    onClick={() => deleteAudioOnly(jobDetails.id)}
                                >
                                    <i className="fas fa-trash-alt"></i> Delete Audio (Keep Transcript)
                                </button>
                            </>
                        ) : (
                            <p className="AudioTranscription__audio-deleted-notice">
                                <i className="fas fa-info-circle"></i> Audio file was deleted on {formatDate(jobDetails.audio_deleted_at)}
                            </p>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="AudioTranscription__page AudioTranscription__page--no-sidebar">
            <div className="AudioTranscription__container">
                <div className="AudioTranscription__page-header">
                    <button 
                        className="AudioTranscription__back-button"
                        onClick={() => navigate('/')}
                        title="Back to home"
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1>
                        <i className="fas fa-microphone"></i> Audio Transcription
                    </h1>
                    <p className="AudioTranscription__page-description">
                        Record or upload audio to get transcriptions and AI-powered summaries.
                    </p>
                </div>

                {/* Free tier notice */}
                <div className="AudioTranscription__free-notice">
                    <i className="fas fa-gift"></i>
                    <div>
                        <strong>This feature is completely free!</strong>
                        <p>
                            Because this is a free service, transcription and AI analysis may take several minutes 
                            to complete, especially for longer recordings. Feel free to navigate away — we'll 
                            process your audio in the background and the results will be ready when you return.
                        </p>
                    </div>
                </div>

                {error && <div className="AudioTranscription__error-message">{error}</div>}

                {/* Summarization Instructions */}
                <div className="AudioTranscription__action-section">
                    <h2><i className="fas fa-magic"></i> Summarization Instructions</h2>
                    <p className="AudioTranscription__section-description">
                        Optionally describe what kind of audio this is and how you'd like it summarized. 
                        For example: "These are meeting notes — list action items and key decisions" or 
                        "This is a lecture — organize by topic with bullet points."
                    </p>
                    <textarea
                        className="AudioTranscription__instructions-input"
                        value={summarizationInstructions}
                        onChange={(e) => setSummarizationInstructions(e.target.value)}
                        placeholder='e.g. "Meeting notes — summarize with action items, decisions, and follow-ups"'
                        rows={3}
                        maxLength={1000}
                        disabled={uploading || isRecording}
                    />
                    <small className="AudioTranscription__instructions-hint">
                        {summarizationInstructions.length}/1000 characters
                    </small>
                </div>

                {/* Recording Section */}
                <div className="AudioTranscription__action-section">
                    <h2>Record Audio</h2>
                    <p className="AudioTranscription__section-description">
                        Record up to 1 hour of audio directly from your microphone.
                    </p>

                    <div className="AudioTranscription__recorder">
                        {!isRecording && !audioBlob && (
                            <button
                                onClick={startRecording}
                                className="AudioTranscription__record-button"
                                disabled={uploading}
                            >
                                <i className="fas fa-microphone"></i> Start Recording
                            </button>
                        )}

                        {isRecording && (
                            <div className="AudioTranscription__recording-active">
                                <div className="AudioTranscription__recording-indicator">
                                    <span className="AudioTranscription__pulse"></span>
                                    Recording...
                                </div>
                                <div className="AudioTranscription__recording-time">
                                    {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_SECONDS)}
                                </div>
                                <div className="AudioTranscription__recording-progress">
                                    <div 
                                        className="AudioTranscription__recording-progress-bar"
                                        style={{ width: `${(recordingTime / MAX_RECORDING_SECONDS) * 100}%` }}
                                    ></div>
                                </div>
                                <button
                                    onClick={stopRecording}
                                    className="AudioTranscription__stop-button"
                                >
                                    <i className="fas fa-stop"></i> Stop Recording
                                </button>
                            </div>
                        )}

                        {audioBlob && !isRecording && (
                            <div className="AudioTranscription__recorded-audio">
                                <div className="AudioTranscription__audio-preview">
                                    <audio controls src={audioUrl}>
                                        Your browser does not support audio playback.
                                    </audio>
                                    <span className="AudioTranscription__audio-duration">
                                        Duration: {formatTime(recordingTime)}
                                    </span>
                                </div>
                                <div className="AudioTranscription__audio-actions-row">
                                    <button
                                        onClick={uploadRecording}
                                        className="AudioTranscription__upload-button"
                                        disabled={uploading}
                                    >
                                        {uploading ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin"></i> Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-cloud-upload-alt"></i> Submit for Transcription
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={discardRecording}
                                        className="AudioTranscription__discard-button"
                                        disabled={uploading}
                                    >
                                        <i className="fas fa-trash"></i> Discard
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* File Upload */}
                    <div className="AudioTranscription__upload-section">
                        <div className="AudioTranscription__divider">
                            <span>OR</span>
                        </div>
                        <label className={`AudioTranscription__file-upload-label${uploading ? ' AudioTranscription__file-upload-label--uploading' : ''}`}>
                            <input
                                type="file"
                                accept=".mp3,.wav,.m4a,.ogg,.flac,.webm"
                                onChange={handleFileUpload}
                                disabled={uploading || isRecording}
                            />
                            {uploading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin AudioTranscription__upload-spinner"></i>
                                    <span>Uploading...</span>
                                    {uploadingFileName && (
                                        <small className="AudioTranscription__uploading-filename" title={uploadingFileName}>
                                            {uploadingFileName.length > 40
                                                ? uploadingFileName.substring(0, 40) + '...'
                                                : uploadingFileName}
                                        </small>
                                    )}
                                    <small>Please wait — this may take a moment for larger files</small>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-file-audio"></i>
                                    <span>Upload Audio File</span>
                                    <small>MP3, WAV, M4A, OGG, FLAC, WEBM (max 100MB, 1 hour)</small>
                                </>
                            )}
                        </label>
                        <p className="AudioTranscription__processing-notice">
                            <i className="fas fa-shield-alt"></i>{" "}
                            Audio is transcribed and summarized by third-party AI services and the recording is
                            deleted from our servers once processing finishes. See our{" "}
                            <Link to="/privacy-policy">Privacy Policy</Link>.
                        </p>
                    </div>
                </div>

                {/* Jobs List and Details */}
                <div className="AudioTranscription__content-layout">
                    <div className="AudioTranscription__jobs-section">
                        <h2>Transcription History</h2>
                        {loading ? (
                            <div className="AudioTranscription__loading-spinner">
                                <i className="fas fa-spinner fa-spin"></i> Loading...
                            </div>
                        ) : jobs.length === 0 ? (
                            <p className="AudioTranscription__no-jobs">
                                No transcriptions yet. Record or upload audio to get started!
                            </p>
                        ) : (
                            <div className="AudioTranscription__jobs-list">
                                {jobs.map((job) => (
                                    <div
                                        key={job.id}
                                        className={`AudioTranscription__job-card ${selectedJob === job.id ? "AudioTranscription__selected" : ""}`}
                                        onClick={() => handleJobClick(job.id)}
                                    >
                                        <div className="AudioTranscription__job-header">
                                            <span className="AudioTranscription__job-filename" title={job.original_filename}>
                                                <i className="fas fa-file-audio"></i>
                                                {job.original_filename?.length > 25 
                                                    ? job.original_filename.substring(0, 25) + '...' 
                                                    : job.original_filename}
                                            </span>
                                            {getStatusBadge(job.status)}
                                        </div>
                                        <div className="AudioTranscription__job-meta">
                                            <div className="AudioTranscription__job-date">
                                                <i className="far fa-clock"></i> {formatDate(job.created_at)}
                                            </div>
                                            <div className="AudioTranscription__job-info">
                                                {job.duration_seconds && (
                                                    <span>
                                                        <i className="fas fa-hourglass-half"></i> {formatDuration(job.duration_seconds)}
                                                    </span>
                                                )}
                                                <span className="AudioTranscription__separator">•</span>
                                                <span>{formatFileSize(job.file_size)}</span>
                                                {formatLanguage(job.detected_language) && (
                                                    <>
                                                        <span className="AudioTranscription__separator">•</span>
                                                        <span title="Detected language">
                                                            <i className="fas fa-language"></i> {formatLanguage(job.detected_language)}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {job.status !== "pending" && job.status !== "processing" && (
                                            <button
                                                className="AudioTranscription__delete-job-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deleteJob(job.id);
                                                }}
                                                title="Delete job"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="AudioTranscription__details-section">
                        {selectedJob ? (
                            <>
                                {loadingDetails ? (
                                    <div className="AudioTranscription__loading-spinner">
                                        <i className="fas fa-spinner fa-spin"></i> Loading details...
                                    </div>
                                ) : jobDetails ? (
                                    <div className="AudioTranscription__job-details">
                                        <div className="AudioTranscription__details-header">
                                            <h2 title={jobDetails.original_filename}>
                                                <i className="fas fa-file-audio"></i> {jobDetails.original_filename}
                                            </h2>
                                            {getStatusBadge(jobDetails.status)}
                                        </div>
                                        <div className="AudioTranscription__details-meta">
                                            <div className="AudioTranscription__meta-item">
                                                <strong>Created:</strong> {formatDate(jobDetails.created_at)}
                                            </div>
                                            {jobDetails.processing_started_at && (
                                                <div className="AudioTranscription__meta-item">
                                                    <strong>Started:</strong> {formatDate(jobDetails.processing_started_at)}
                                                </div>
                                            )}
                                            {jobDetails.processing_completed_at && (
                                                <div className="AudioTranscription__meta-item">
                                                    <strong>Completed:</strong> {formatDate(jobDetails.processing_completed_at)}
                                                </div>
                                            )}
                                            <div className="AudioTranscription__meta-item">
                                                <strong>File Size:</strong> {formatFileSize(jobDetails.file_size)}
                                            </div>
                                            {jobDetails.duration_seconds && (
                                                <div className="AudioTranscription__meta-item">
                                                    <strong>Duration:</strong> {formatDuration(jobDetails.duration_seconds)}
                                                </div>
                                            )}
                                            {formatLanguage(jobDetails.detected_language) && (
                                                <div className="AudioTranscription__meta-item">
                                                    <strong>Language:</strong> {formatLanguage(jobDetails.detected_language)}
                                                </div>
                                            )}
                                        </div>

                                        {jobDetails.status === "failed" && jobDetails.error_message && (
                                            <div className="AudioTranscription__error-details">
                                                <strong>Error:</strong>
                                                <p>{jobDetails.error_message}</p>
                                            </div>
                                        )}

                                        {jobDetails.status === "completed" && renderResults()}

                                        {(jobDetails.status === "pending" || jobDetails.status === "processing") && (
                                            <div className="AudioTranscription__processing-info">
                                                <i className="fas fa-spinner fa-spin"></i>
                                                <p>
                                                    {jobDetails.status === "pending" 
                                                        ? "Your audio is queued for processing..." 
                                                        : "Transcribing and analyzing your audio..."}
                                                </p>
                                                <small>
                                                    This usually takes a minute or two, longer when the service is busy.
                                                    Feel free to navigate away — your results will be ready when you return.
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="AudioTranscription__no-selection">
                                <i className="fas fa-hand-pointer"></i>
                                <p>Select a transcription from the list to view details</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AudioTranscription;
